import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { GetQueue } from "../../Services/HeedifyServices";
import { parseQueueKpi, calculateTotalKpi, saveLayoutPositions, getLayoutPositions } from "../../Services/RealtimeDashboardService";
import { getAccessToken } from "../../Services/GraphServices";
import QueueCard from "./QueueCard";
import AgentsComponent from "./AgentsComponent";
import AgentStatusWidget from "./AgentStatusWidget";
import QueueListComponent from "./QueueListComponent";
import QueueCardsGrid from "./QueueCardsGrid";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./CSS/QueuesComponent.css";
import { Spinner } from "@fluentui/react-components";
const ResponsiveGridLayout = WidthProvider(Responsive);

const QueuesComponent = ({
  selectedQueues,
  agents,
  connection,
  isConnected,
  isEditMode,
  setIsEditMode,
  setSaveLayoutCallback,
  setResetLayoutCallback,
  setIsLayoutModified
}) => {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const currentTenantId = activeAccount?.tenantId;
  const userId = activeAccount?.localAccountId;
  const [token, setToken] = useState(null);
  const [queueKpis, setQueueKpis] = useState({});
  const [isLoadingKpis, setIsLoadingKpis] = useState(true);
  const [agentsData, setAgentsData] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  // Queue sélectionnée dans le QueueCardsGrid. Quand sélectionnée, les widgets du bas
  // (Calls, Max Wait, Avg Wait, Live, Abandon Rate, Overflow) montrent les KPIs de cette
  // queue uniquement au lieu du total. null = afficher le total agrégé.
  const [selectedQueueId, setSelectedQueueId] = useState(null);
  // Layout configuration for react-grid-layout
    const defaultLayout = {
    lg: [
      { i: 'maxWaitTime', x: 0, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'todayCalls', x: 2, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'overflow', x: 4, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'liveCalls', x: 6, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'abandonRate', x: 8, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'avgWait', x: 10, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'callbacks', x: 0, y: 2, w: 4, h: 2, minW: 2, minH: 2 },
      { i: 'queueList', x: 0, y: 4, w: 6, h: 4, minW: 4, minH: 3 },
      { i: 'agentStatus', x: 6, y: 4, w: 6, h: 4, minW: 3, minH: 3 },
      { i: 'agents', x: 0, y: 8, w: 12, h: 5, minW: 5, minH: 4 },
    ],
    md: [
      { i: 'maxWaitTime', x: 0, y: 0, w: 2, h: 2 },
      { i: 'todayCalls', x: 2, y: 0, w: 2, h: 2 },
      { i: 'overflow', x: 4, y: 0, w: 2, h: 2 },
      { i: 'liveCalls', x: 0, y: 2, w: 2, h: 2 },
      { i: 'abandonRate', x: 2, y: 2, w: 2, h: 2 },
      { i: 'avgWait', x: 4, y: 2, w: 2, h: 2 },
      { i: 'callbacks', x: 0, y: 4, w: 3, h: 2 },
      { i: 'agentStatus', x: 0, y: 6, w: 3, h: 4 },
      { i: 'queueList', x: 0, y: 10, w: 6, h: 4 },
      { i: 'agents', x: 0, y: 12, w: 6, h: 4 },
    ],
    sm: [
      { i: 'maxWaitTime', x: 0, y: 0, w: 2, h: 2 },
      { i: 'todayCalls', x: 2, y: 0, w: 2, h: 2 },
      { i: 'overflow', x: 0, y: 2, w: 2, h: 2 },
      { i: 'liveCalls', x: 2, y: 2, w: 2, h: 2 },
      { i: 'abandonRate', x: 0, y: 4, w: 2, h: 2 },
      { i: 'avgWait', x: 2, y: 4, w: 2, h: 2 },
      { i: 'callbacks', x: 0, y: 6, w: 4, h: 2 },
      { i: 'agentStatus', x: 0, y: 8, w: 4, h: 4 },
      { i: 'queueList', x: 0, y: 12, w: 4, h: 4 },
      { i: 'agents', x: 0, y: 16, w: 4, h: 4 },
    ],
  };

  const [layouts, setLayouts] = useState(defaultLayout);

  // Charger les positions sauvegardées depuis le service
  useEffect(() => {
    const loadLayoutPositions = async () => {
      if (!currentTenantId || !token) return;

      try {
        const response = await getLayoutPositions(currentTenantId, token);

        if (response.success && response.data) {
          const savedPositions = response.data;

          // Appliquer les positions sauvegardées au layout lg
          const updatedLgLayout = defaultLayout.lg.map(item => {
            const savedPos = savedPositions[item.i];
            if (savedPos) {
              return {
                ...item,
                x: savedPos.x,
                y: savedPos.y,
                w: savedPos.w,
                h: savedPos.h
              };
            }
            return item;
          });

          setLayouts({
            ...defaultLayout,
            lg: updatedLgLayout
          });
        }
      } catch (error) {
        console.error('Error loading layout positions:', error);
      }
    };

    loadLayoutPositions();
  }, [currentTenantId, token]);

  // Enregistrer le callback de sauvegarde
  useEffect(() => {
    if (setSaveLayoutCallback) {
      setSaveLayoutCallback(() => () => {
        // Collecter les positions actuelles du layout lg
        const currentPositions = {};

        if (layouts && layouts.lg) {
          layouts.lg.forEach(item => {
            currentPositions[item.i] = {
              id: item.i,
              x: item.x,
              y: item.y,
              w: item.w,
              h: item.h
            };
          });
        }

        saveDashboardPositions(currentPositions);
      });
    }
  }, [layouts, setSaveLayoutCallback]);

  // Enregistrer le callback de reset
  useEffect(() => {
    if (setResetLayoutCallback) {
      setResetLayoutCallback(() => () => {
        // Réinitialiser au layout par défaut
        setLayouts(defaultLayout);
        console.log('Layout reset to default');
      });
    }
  }, [setResetLayoutCallback]);

  const saveDashboardPositions = async (positions) => {
    try {
      if (!currentTenantId || !token) {
        console.log('Missing required data for saving layout');
        return;
      }

      // Sauvegarder via le nouveau service
      const response = await saveLayoutPositions(currentTenantId, positions, token);

      if (response.success) {
        console.log('Dashboard positions saved successfully:', response.message);
      } else {
        console.error('Error saving dashboard positions:', response.message);
      }
    } catch (error) {
      console.error('Error saving dashboard positions:', error);
    }
  };

  const handleLayoutChange = (layout, allLayouts) => {
    setLayouts(allLayouts);

    // Marquer le layout comme modifié en mode édition
    if (isEditMode && setIsLayoutModified) {
      setIsLayoutModified(true);
    }

    // Afficher le JSON du layout en console en mode édition
    if (isEditMode && allLayouts.lg) {
      const layoutPositions = {};
      allLayouts.lg.forEach(item => {
        layoutPositions[item.i] = {
          id: item.i,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h
        };
      });
      console.log('Layout Positions JSON:', JSON.stringify(layoutPositions, null, 2));
    }
  };

  // selectedQueues contient déjà les objets complets des queues
  const queuesArray = useMemo(() => {
    if (!selectedQueues || selectedQueues.length === 0) return [];
    return selectedQueues;
  }, [selectedQueues]);

  // Extraire les scopes uniques des queues sélectionnées
  const scopesFromQueues = useMemo(() => {
    const scopes = new Set();
    queuesArray.forEach(queue => {
      if (queue.scope) scopes.add(queue.scope);
    });
    return Array.from(scopes);
  }, [queuesArray]);

  // Utiliser le premier scope disponible pour tenantId
  const tenantId = scopesFromQueues.length > 0 ? scopesFromQueues[0] : currentTenantId;
  const scopeName = scopesFromQueues.length > 0 ? scopesFromQueues[0] : 'default';

  // Étape 1: Acquérir le token via getAccessToken
  useEffect(() => {
    const fetchToken = async () => {
      if (activeAccount && instance) {
        try {
          const accessToken = await getAccessToken(instance, activeAccount);
          setToken(accessToken);
        } catch (error) {
          console.error("Error acquiring token:", error);
        }
      }
    };

    fetchToken();
  }, [instance, activeAccount]);



  // Fusionner et calculer le KPI total de toutes les queues sélectionnées
  const totalKpi = useMemo(() => {
    // calculateTotalKpi attend un objet {queueId: kpi}, pas un array
    return calculateTotalKpi(queueKpis);
  }, [queueKpis]);

  // KPI affiché dans les widgets du bas : queue sélectionnée OU total agrégé.
  // parseQueueKpi retourne les metrics au top level → on les wrap dans { kpi: ... }
  // pour matcher le format de totalKpi.
  const displayedKpi = useMemo(() => {
    if (selectedQueueId && queueKpis[selectedQueueId]) {
      return { kpi: queueKpis[selectedQueueId] };
    }
    return totalKpi;
  }, [selectedQueueId, queueKpis, totalKpi]);

  // Sélection par défaut : première queue dès qu'elles sont chargées
  useEffect(() => {
    if (!selectedQueueId && queuesArray && queuesArray.length > 0) {
      const firstId = queuesArray[0].id || queuesArray[0].RowKey;
      setSelectedQueueId(firstId);
    }
  }, [queuesArray, selectedQueueId]);

  // Combiner tous les agentDetails de toutes les queues
  const allAgentDetails = useMemo(() => {
    const combinedAgentDetails = {};

    Object.values(queueKpis).forEach(kpi => {
      if (kpi.agentDetails) {
        Object.entries(kpi.agentDetails).forEach(([agentId, agentData]) => {
          // Si l'agent existe déjà, on garde la dernière mise à jour
          if (!combinedAgentDetails[agentId] ||
            (agentData.lastUpdated && agentData.lastUpdated > (combinedAgentDetails[agentId].lastUpdated || 0))) {
            combinedAgentDetails[agentId] = agentData;
          }
        });
      }
    });

    return combinedAgentDetails;
  }, [queueKpis]);

  // NB : il y avait ici un `allReasonCodes` qui concaténait les activités configurées de
  // TOUTES les files, sans dédoublonner, et les passait au donut de disponibilité. Ce
  // dernier les comptait comme des agents : une activité configurée sur 3 files produisait
  // 3 agents fantômes. Le donut se déduit désormais uniquement des agents réels.

  // Concaténer tous les agentIds uniques des queues sélectionnées
  // Garder en mémoire tous les agents déjà vus pour éviter qu'ils disparaissent
  const allAgentIds = useMemo(() => {
    const agentIdsSet = new Set();

    Object.values(queueKpis).forEach(kpi => {
      if (kpi.agentDetails) {
        Object.keys(kpi.agentDetails).forEach(agentId => {
          agentIdsSet.add(agentId);
        });
      }
    });

    return Array.from(agentIdsSet);
  }, [queueKpis]);

  // Garder en mémoire tous les agents déjà chargés pour éviter qu'ils disparaissent
  const [allSeenAgentIds, setAllSeenAgentIds] = useState(new Set());
  
  useEffect(() => {
    if (allAgentIds.length > 0) {
      setAllSeenAgentIds(prev => {
        const updated = new Set(prev);
        allAgentIds.forEach(id => updated.add(id));
        return updated;
      });
    }
  }, [allAgentIds]);

  // Utiliser les IDs vus pour charger les agents
  const agentIdsToLoad = useMemo(() => {
    const ids = Array.from(allSeenAgentIds);
    console.log(`📋 [Agents] Total unique agent IDs to load: ${ids.length}`, ids);
    return ids;
  }, [allSeenAgentIds]);

  // Étape 2: Charger les données en temps réel pour chaque queue individuellement
  useEffect(() => {
    // Attendre que le token soit disponible
    if (!token) {
      return;
    }

    if (!currentTenantId || !userId || !queuesArray || queuesArray.length === 0) {
      console.log("Missing required data:", { currentTenantId, userId, queuesArrayLength: queuesArray?.length });
      // Réinitialiser queueKpis si aucune queue n'est sélectionnée
      setQueueKpis({});
      setIsLoadingKpis(false);
      return;
    }


    const loadQueuesData = async () => {
      setIsLoadingKpis(true);
      // Réinitialiser queueKpis pour ne garder que les queues sélectionnées
      setQueueKpis({});

      try {
        // Créer un objet temporaire pour stocker tous les KPI
        const newQueueKpis = {};

        // Mapper chaque queue et appeler GetQueue individuellement
        const queuePromises = queuesArray.map(async (queue) => {
          const queueId = queue.id || queue.RowKey;
          const scope = queue.scope;

          try {
            // Appeler GetQueue pour récupérer les données de cette queue
            const queueData = await GetQueue(currentTenantId, userId, queueId, scope, token);
console.log(`Data for queue ${queueId}:`, queueData);
            if (queueData) {
              // Parser les KPI de cette queue
              const parsedKpi = parseQueueKpi(queueData);
              newQueueKpis[queueId] = parsedKpi;
            }
          } catch (error) {
            console.error(`Error loading queue ${queueId}:`, error);
          }
        });

        // Attendre que toutes les queues soient chargées
        await Promise.all(queuePromises);

        // Mettre à jour le state une seule fois avec toutes les queues
        setQueueKpis(newQueueKpis);
        setIsLoadingKpis(false);
      } catch (err) {
        console.error("Error loading queues data:", err);
        setIsLoadingKpis(false);
      }
    };

    loadQueuesData();
  }, [token, currentTenantId, userId, queuesArray]);

  // S'abonner aux mises à jour SignalR pour toutes les queues
  useEffect(() => {
    if (!connection || !isConnected || !queuesArray || queuesArray.length === 0) return;

    const subscriptions = [];
    const registeredEvents = new Set(); // Éviter les doublons d'enregistrement

    queuesArray.forEach(queue => {
      const queueId = queue.id || queue.RowKey;
      // Le serveur envoie l'événement avec le nom exact de la queue (sans préfixe)
      const eventName = queueId;

      // Éviter d'enregistrer le même événement plusieurs fois
      if (registeredEvents.has(eventName)) {
        console.warn(`Event ${eventName} already registered, skipping duplicate`);
        return;
      }

      const handler = (updatedKpi) => {
        console.log(`📊 [SignalR] Queue ${queueId} - Event received:`, updatedKpi);
       
        setQueueKpis(prev => {
          const existingKpi = prev[queueId];
          
          // Si l'événement ne contient pas agentDetails ET qu'on a des données existantes
          // Injecter les anciens agentDetails AVANT de parser pour que parseQueueKpi calcule correctement les états
          if ((!updatedKpi.agentDetails || Object.keys(updatedKpi.agentDetails).length === 0) && existingKpi?.agentDetails) {
            console.log(`⚠️ [SignalR] Queue ${queueId} - Event without agentDetails, using previous agentDetails`);
            updatedKpi.agentDetails = existingKpi.agentDetails;
          }
          
          // Parser avec les agentDetails (nouveaux ou préservés)
          return {
            ...prev,
            [queueId]: parseQueueKpi(updatedKpi)
          };
        });
      };

      connection.on(eventName, handler);
      subscriptions.push({ eventName, handler });
      registeredEvents.add(eventName);
      console.log(`✅ [SignalR] Subscribed to queue event: ${eventName}`);
    });

    return () => {
      subscriptions.forEach(({ eventName, handler }) => {
        connection.off(eventName, handler);
        console.log(`❌ [SignalR] Unsubscribed from queue event: ${eventName}`);
      });
    };
  }, [connection, isConnected, queuesArray]);

  // Étape 3: Construire les données des agents depuis agentDetails (sans appel API)
  useEffect(() => {
    if (agentIdsToLoad.length === 0) {
      setAgentsData([]);
      return;
    }

    // Construire les données des agents directement depuis queueKpis.agentDetails
    const agents = agentIdsToLoad.map(agentId => {
      // Trouver les infos de l'agent dans toutes les queues et garder l'état le plus récent
      let agentInfo = null;
      let latestUpdate = 0;
      const agentQueues = [];

      Object.values(queueKpis).forEach(kpi => {
        if (kpi.agentDetails && kpi.agentDetails[agentId]) {
          const details = kpi.agentDetails[agentId];
          
          // Garder l'état le plus récent basé sur lastUpdated
          const updateTime = details.lastUpdated || 0;
          if (!agentInfo || updateTime > latestUpdate) {
            latestUpdate = updateTime;
            agentInfo = {
              id: agentId,
              name: details.displayName,
              email: details.email,
              // Statut EFFECTIF (liveState), repli sur le déclaré (state) : un agent peut rester
              // state="READY" alors que Teams le voit Busy → liveState="UNAVAILABLE".
              state: details.liveState ?? details.state
            };
          }

          // Ajouter la queue à la liste
          agentQueues.push({
            queueId: kpi.queueId,
            queueName: kpi.queueName,
            state: details.liveState ?? details.state
          });
        }
      });

      return agentInfo ? { ...agentInfo, queues: agentQueues } : null;
    }).filter(agent => agent !== null);

    setAgentsData(agents);
    setIsLoadingAgents(false);
  }, [agentIdsToLoad, queueKpis]);

  // Fonction pour formater les pourcentages
  const formatPercentage = (value) => {
    if (value == null || value === "--" || value === 0) return "0%";
    const numberValue = parseFloat(value);
    if (Number.isInteger(numberValue)) {
      return `${numberValue}%`;
    }
    const formattedValue = numberValue.toFixed(2).replace(/\.?0+$/, "");
    return `${formattedValue}%`;
  };

  return (
    <div className="queues-component">
      {/* Queue Cards Grid — 1 carte par queue, layout responsive auto.
          Click sur une card → met à jour les widgets KPI du bas avec les données de cette queue. */}
      <QueueCardsGrid
        queues={queuesArray}
        queueKpis={queueKpis}
        selectedQueueId={selectedQueueId}
        onSelectQueue={setSelectedQueueId}
      />

      {/* KPI Cards Grid */}
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 996, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 6, sm: 4, xs: 2, xxs: 1 }}
        rowHeight={60}
        onLayoutChange={handleLayoutChange}
        isDraggable={isEditMode}
        isResizable={isEditMode}
      >
        {/* Max Wait Time Widget */}
        <div key="maxWaitTime" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.MAX_WAIT_TIME")}</h3>

          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="large" />
            ) : (
              displayedKpi?.kpi?.maxWaitTime || "00:00:00"
            )}
          </div>
        </div>

        {/* Today Calls Card */}
        <div key="todayCalls" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.CALLS_IN_QUEUE")}</h3>
          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="small" />
            ) : (
              displayedKpi?.kpi?.todayCalls || 0
            )}
          </div>
          {!isLoadingKpis && <div className="kpi-card-extra today">{t("REALTIME.TODAY")}</div>}
        </div>

        {/* Overflow Widget */}
        <div key="overflow" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.OVERFLOW")}</h3>
          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="small" />
            ) : (
              formatPercentage(displayedKpi?.kpi?.overflowRate)
            )}
          </div>
        </div>

        {/* Live Calls Card */}
        <div key="liveCalls" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.LIVE_CALLS")}</h3>
          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="small" />
            ) : (
              displayedKpi?.kpi?.callsInQueue || 0
            )}
          </div>
          {!isLoadingKpis && <div className="kpi-card-extra live">{t("REALTIME.LIVE")}</div>}
        </div>

        {/* Abandon Rate Card */}
        <div key="abandonRate" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.ABANDON_RATE")}</h3>
          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="small" />
            ) : (
              formatPercentage(displayedKpi?.kpi?.abandonRate)
            )}
          </div>
        </div>

        {/* Average Wait Time Widget */}
        <div key="avgWait" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.AVG_WAIT_TIME")}</h3>
          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="small" />
            ) : (
              displayedKpi?.kpi?.avgWaitTime || "00:00:00"
            )}
          </div>
        </div>

        {/* RAPPEL EN FILE — n'existe que si au moins une file l'a activé.
            Le rendu conditionnel n'est pas cosmétique : une entrée de layout sans enfant est
            ignorée par react-grid-layout, donc un tenant qui n'utilise pas le rappel ne voit
            ni case vide ni décalage. Afficher « 0 rappel » à une file qui n'en fait pas serait
            une information fausse, pas une information neutre. */}
        {displayedKpi?.kpi?.callbacks && (
          <div key="callbacks" className="kpi-card">
            <div className="kpi-card-header drag-handle">
              <button className="kpi-card-menu">⋮⋮</button>
              <h3>{t("REALTIME.CALLBACKS", { defaultValue: "Callbacks" })}</h3>
            </div>
            {isLoadingKpis ? (
              <div className="kpi-card-value"><Spinner size="small" /></div>
            ) : (
              <>
                {/* Le chiffre principal est le nombre de promesses VIVANTES — c'est la charge
                    qui reste à honorer, la seule qu'un superviseur puisse encore influencer.
                    Le cumul du jour est une trace, pas une action : il passe en second. */}
                <div className="kpi-card-value">{displayedKpi.kpi.callbacks.total || 0}</div>
                <div className="kpi-card-extra">
                  {/* En risque = l'échéance approche ou est dépassée. C'est le seul chiffre de
                      cette carte sur lequel on agit dans la minute, d'où sa mise en avant. */}
                  {displayedKpi.kpi.callbacks.atRisk > 0 && (
                    <span style={{ color: '#D13438', fontWeight: 700, marginRight: 10 }}>
                      {displayedKpi.kpi.callbacks.atRisk} {t("REALTIME.CALLBACKS_AT_RISK", { defaultValue: "at risk" })}
                    </span>
                  )}
                  <span style={{ marginRight: 10 }}>
                    {displayedKpi.kpi.callbacks.dueToday || 0} {t("REALTIME.CALLBACKS_DUE_TODAY", { defaultValue: "due today" })}
                  </span>
                  {/* Prochaine échéance, toutes files confondues : la PLUS PROCHE, pas une
                      moyenne. `ts_` est une époque en secondes (convention Data_Pipe). */}
                  {displayedKpi.kpi.callbacks.ts_nextDueAt && (
                    <span>
                      {t("REALTIME.CALLBACKS_NEXT_DUE", { defaultValue: "next" })}{' '}
                      {new Date(displayedKpi.kpi.callbacks.ts_nextDueAt * 1000)
                        .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Availability Widget — suit la queue sélectionnée (fallback total si aucune) */}
        {(() => {
          const selectedQ = selectedQueueId ? queueKpis[selectedQueueId] : null;
          const agentStats = selectedQ?.agents || totalKpi?.agents;
          const agentDetails = selectedQ?.agentDetails || allAgentDetails;
          if (!agentStats) return null;
          return (
            <div key="agentStatus" className="grid-widget">
              {isLoadingKpis ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Spinner size="medium" />
                </div>
              ) : (
                <AgentStatusWidget
                  agentStats={agentStats}
                  agentDetails={agentDetails}
                />
              )}
            </div>
          );
        })()}

        {/* Queue List */}
        <div key="queueList" className="grid-widget">
          {isLoadingKpis ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Spinner size="medium" />
            </div>
          ) : (
            <QueueListComponent
              queues={queuesArray}
              queueKpis={queueKpis}
            />
          )}
        </div>

        {/* Agents Widget */}
        <div key="agents" className="grid-widget agents-widget">
          <h3>{t("REALTIME.AGENTS")} ({agentsData.length}) <span className="drag-handle">⋮⋮</span></h3>
          <AgentsComponent
            tenantId={tenantId}
            agents={agentsData}
            isLoading={isLoadingAgents}
            connection={connection}
            isConnected={isConnected}
          />
        </div>
      </ResponsiveGridLayout>
    </div>
  );
};

export default QueuesComponent;
